import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Info } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LoadingReport from "@/components/LoadingReport";
import { generateReport } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Intake() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    symptoms: "",
    health_goals: "",
    age: "",
    sex: "",
    current_conditions: "",
    current_medications: "",
  });

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.symptoms.trim()) {
      toast.error("Please describe your symptoms.");
      return;
    }
    setLoading(true);
    const payload = {
      symptoms: form.symptoms.trim(),
      ...(form.health_goals.trim() && { health_goals: form.health_goals.trim() }),
      ...(form.age && { age: Number(form.age) }),
      ...(form.sex && form.sex !== "prefer" && { sex: form.sex }),
      ...(form.current_conditions.trim() && { current_conditions: form.current_conditions.trim() }),
      ...(form.current_medications.trim() && { current_medications: form.current_medications.trim() }),
    };
    try {
      const report = await generateReport(payload);
      navigate(`/r/${report.report_id}`);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't generate report. Please try again.");
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <>
        <Header variant="app" />
        <LoadingReport />
      </>
    );
  }

  return (
    <>
      <Header variant="app" />
      <section className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue">Intake</span>
        <h1 className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight text-ink-900">
          Tell us what you&apos;re experiencing
        </h1>
        <p className="mt-3 text-ink-600 max-w-xl">
          The more you share, the more relevant your recommended tests will be. Only symptoms are required.
        </p>

        <div className="mt-6 flex items-start gap-3 rounded-2xl bg-brand-blue-light/60 border border-brand-blue-light p-4">
          <Info className="h-5 w-5 shrink-0 text-brand-blue mt-0.5" strokeWidth={2.2} />
          <p className="text-sm text-ink-700 leading-relaxed">
            This tool does not provide medical advice. It's meant to prep you for a conversation with your practitioner.
          </p>
        </div>

        <form
          data-testid="intake-form"
          onSubmit={onSubmit}
          className="mt-10 space-y-6 rounded-3xl bg-white border border-slate-100 p-6 md:p-8 shadow-sm"
        >
          <div>
            <Label htmlFor="symptoms" className="font-heading text-base text-ink-900">
              Symptoms <span className="text-brand-blue">*</span>
            </Label>
            <Textarea
              id="symptoms"
              data-testid="intake-symptoms"
              required
              placeholder="Describe what you're experiencing — fatigue, brain fog, joint pain, headaches, GI issues, etc. Include duration and severity if relevant."
              value={form.symptoms}
              onChange={(e) => update("symptoms", e.target.value)}
              className="mt-2 min-h-[120px] rounded-xl"
            />
          </div>

          <div>
            <Label htmlFor="goals" className="font-heading text-base text-ink-900">
              Health goals <span className="text-ink-500 text-sm font-normal">(optional)</span>
            </Label>
            <Textarea
              id="goals"
              data-testid="intake-goals"
              placeholder="e.g. figure out the root cause, optimize longevity, prepare for a specialist visit..."
              value={form.health_goals}
              onChange={(e) => update("health_goals", e.target.value)}
              className="mt-2 min-h-[80px] rounded-xl"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="age" className="font-heading text-base text-ink-900">
                Age <span className="text-ink-500 text-sm font-normal">(optional)</span>
              </Label>
              <Input
                id="age"
                data-testid="intake-age"
                type="number"
                min={0}
                max={130}
                value={form.age}
                onChange={(e) => update("age", e.target.value)}
                className="mt-2 rounded-xl"
                placeholder="42"
              />
            </div>
            <div>
              <Label htmlFor="sex" className="font-heading text-base text-ink-900">
                Sex <span className="text-ink-500 text-sm font-normal">(optional)</span>
              </Label>
              <Select value={form.sex} onValueChange={(v) => update("sex", v)}>
                <SelectTrigger id="sex" data-testid="intake-sex" className="mt-2 rounded-xl">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                  <SelectItem value="prefer">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="conditions" className="font-heading text-base text-ink-900">
              Current conditions <span className="text-ink-500 text-sm font-normal">(optional)</span>
            </Label>
            <Textarea
              id="conditions"
              data-testid="intake-conditions"
              placeholder="e.g. hypothyroidism, PCOS, hypertension..."
              value={form.current_conditions}
              onChange={(e) => update("current_conditions", e.target.value)}
              className="mt-2 min-h-[70px] rounded-xl"
            />
          </div>

          <div>
            <Label htmlFor="meds" className="font-heading text-base text-ink-900">
              Current medications <span className="text-ink-500 text-sm font-normal">(optional)</span>
            </Label>
            <Textarea
              id="meds"
              data-testid="intake-meds"
              placeholder="e.g. levothyroxine 50mcg, metformin 500mg..."
              value={form.current_medications}
              onChange={(e) => update("current_medications", e.target.value)}
              className="mt-2 min-h-[70px] rounded-xl"
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
            <p className="text-xs text-ink-500 max-w-md leading-relaxed">
              By continuing you acknowledge this is educational only and not a substitute for medical advice.
            </p>
            <Button
              type="submit"
              data-testid="intake-submit"
              className="rounded-full bg-brand-blue text-white hover:bg-brand-blue-hover px-6 py-6 h-auto font-semibold shadow-md hover:-translate-y-0.5 active:scale-95 transition-transform"
            >
              Get my workup
              <ArrowRight className="h-4 w-4 ml-1" strokeWidth={2.4} />
            </Button>
          </div>
        </form>
      </section>
      <Footer />
    </>
  );
}
